import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DatePicker, Spin } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ROLL_CALL_API_BASE_URL } from "../RollCallConfig/RollCallConfig";

const OrgDashboard = () => {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [branches, setBranches] = useState([]);
  const [branchTokens, setBranchTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [perm, setPerm] = useState({
    Read: false,
    Write: false,
    Delete: false,
    Export: false,
  });
  console.log(perm);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const stored = sessionStorage.getItem("rollcall-permissions");
    if (!stored) return;

    const map = JSON.parse(stored);
    const path = location.pathname.slice(1);
    const perms = map[path];
    if (!perms) {
      navigate("/rollcall/dashboard");
      return;
    }
    if (perms) {
      setPerm({
        Read: perms.canRead,
        Write: perms.canWrite,
        Delete: perms.canDelete,
        Export: perms.canExport,
      });
    }
  }, []);
  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const superId = sessionStorage.getItem("superId");
    if (!token || !superId) {
      navigate("/signin");
    }
  }, [navigate]);

  useEffect(() => {
    const fetchBranchTokens = async () => {
      const authToken = sessionStorage.getItem("authToken");
      const userData = JSON.parse(sessionStorage.getItem("userData") || "{}");
      const userId = userData?.UserId;

      if (!authToken || !userId) return;

      try {
        const response = await fetch(
          `${ROLL_CALL_API_BASE_URL}portal/user/orgsignin`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({ userid: userId }),
          },
        );
        const data = await response.json();
        if (data?.status) {
          setBranchTokens(data.ResultData); // [{ SuperId, OrgName, Token, HasLicense, ... }]
        } else {
          console.error("orgsignin failed:", data);
        }
      } catch (error) {
        console.error("Error fetching branch tokens:", error.message);
      }
    };

    fetchBranchTokens();
  }, []);

  useEffect(() => {
    if (branchTokens.length === 0) return;

    const fetchOrgDashboard = async () => {
      const authToken = sessionStorage.getItem("authToken");
      if (!authToken) return;

      setLoading(true);
      try {
        const formattedDate = dayjs(selectedDate).format("YYYY-MM-DD");
        const superIds = branchTokens.map((bt) => bt.SuperId).join(",");

        const response = await fetch(
          `${ROLL_CALL_API_BASE_URL}portal/Dashboard/GetOrgDashboardCount?superids=${superIds}&selectedDate=${formattedDate}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
          },
        );
        const data = await response.json();
        if (data?.status) {
          setBranches(data.ResultData);
        } else {
          console.error("Failed to fetch org dashboard:", data);
        }
      } catch (error) {
        console.error("Error fetching org dashboard:", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrgDashboard();
  }, [selectedDate, branchTokens]); // branchTokens as dependency

  const orgSummary = branches.reduce(
    (acc, branch) => ({
      totalBranches: acc.totalBranches + 1,
      totalRegistrations:
        acc.totalRegistrations + (branch.TotalRegistrations || 0),
      totalPresent: acc.totalPresent + (branch.Present || 0),
      totalLate: acc.totalLate + (branch.Late || 0),
      totalAbsent: acc.totalAbsent + (branch.Absent || 0),
    }),
    {
      totalBranches: 0,
      totalRegistrations: 0,
      totalPresent: 0,
      totalLate: 0,
      totalAbsent: 0,
    },
  );

  const handleShowPortal = (branch) => {
    const branchAuth = branchTokens.find(
      (bt) => String(bt.SuperId) === String(branch.SuperId),
    );

    if (!branchAuth?.Token) {
      console.warn("No token found for branch:", branch.SuperId);
      return;
    }

    sessionStorage.setItem("authToken", branchAuth.Token);
    sessionStorage.setItem("superId", branchAuth.SuperId);
    sessionStorage.setItem("branchName", branchAuth.OrgName);

    // navigate("/rollcall/dashboard");
    window.open("/rollcall/dashboard", "_blank");
  };

  // const getTodayDate = () => {
  //   const today = new Date();
  //   return `${String(today.getDate()).padStart(2, "0")}/${String(today.getMonth() + 1).padStart(2, "0")}/${today.getFullYear()}`;
  // };

  return (
    <>
      {perm.Read ? (
        <div className="page-wrapper">
          {loading && (
            <div
              className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
              style={{
                zIndex: 1050,
                backdropFilter: "blur(4px)",
                backgroundColor: "rgba(255,255,255,0.2)",
              }}
            >
              <Spin size="large" tip="Loading..." />
            </div>
          )}

          <div className="content">
            {/* Page Header */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
              <div>
                <h1 className="mb-1">Organization Dashboard</h1>
                <p className="fw-medium">
                  All Branches Overview &nbsp;|&nbsp; (
                  {selectedDate ? selectedDate.format("DD-MM-YYYY") : ""})
                </p>
              </div>
              <div className="input-icon-start position-relative mb-3">
                <DatePicker
                  size="small"
                  suffixIcon={<CalendarOutlined />}
                  format="DD-MM-YYYY"
                  allowClear={false}
                  value={
                    selectedDate ? dayjs(selectedDate, "DD-MM-YYYY") : null
                  }
                  presets={[
                    { label: "Today", value: dayjs() },
                    { label: "Yesterday", value: dayjs().subtract(1, "day") },
                  ]}
                  onChange={(date) => setSelectedDate(date)}
                />
              </div>
            </div>

            <div className="row g-3 mb-4 d-none">
              <div className="col-xl col-sm-6 col-12 d-flex">
                <div className="card bg-secondary sale-widget flex-fill">
                  <div className="card-body d-flex align-items-center">
                    <span className="sale-icon bg-white text-secondary">
                      <i className="ti ti-building fs-24" />
                    </span>
                    <div className="ms-2">
                      <p className="text-white mb-1">Total Branches</p>
                      <h4 className="text-white">{orgSummary.totalBranches}</h4>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl col-sm-6 col-12 d-flex">
                <div
                  className="card sale-widget flex-fill"
                  style={{ backgroundColor: "#6366f1" }}
                >
                  <div className="card-body d-flex align-items-center">
                    <span
                      className="sale-icon bg-white"
                      style={{ color: "#6366f1" }}
                    >
                      <i className="ti ti-file-text fs-24" />
                    </span>
                    <div className="ms-2">
                      <p className="text-white mb-1">Registrations</p>
                      <h4 className="text-white">
                        {orgSummary.totalRegistrations}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl col-sm-6 col-12 d-flex">
                <div className="card bg-teal sale-widget flex-fill">
                  <div className="card-body d-flex align-items-center">
                    <span className="sale-icon bg-white text-teal">
                      <i className="fa-solid fa-user-check fa-lg" />
                    </span>
                    <div className="ms-2">
                      <p className="text-white mb-1">Total Present</p>
                      <h4 className="text-white">{orgSummary.totalPresent}</h4>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl col-sm-6 col-12 d-flex">
                <div className="card bg-primary sale-widget flex-fill">
                  <div className="card-body d-flex align-items-center">
                    <span className="sale-icon bg-white text-primary">
                      <i className="fa-solid fa-user-clock fa-lg" />
                    </span>
                    <div className="ms-2">
                      <p className="text-white mb-1">Total Late</p>
                      <h4 className="text-white">{orgSummary.totalLate}</h4>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl col-sm-6 col-12 d-flex">
                <div className="card bg-danger sale-widget flex-fill">
                  <div className="card-body d-flex align-items-center">
                    <span className="sale-icon bg-white text-danger">
                      <i className="fa-solid fa-user-xmark fa-lg" />
                    </span>
                    <div className="ms-2">
                      <p className="text-white mb-1">Total Absent</p>
                      <h4 className="text-white">{orgSummary.totalAbsent}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3">
              {!loading && branches.length === 0 && (
                <div className="col-12">
                  <div className="card">
                    <div className="card-body text-center py-5 text-muted">
                      <i className="ti ti-building-off fs-1 d-block mb-2" />
                      No branches found.
                    </div>
                  </div>
                </div>
              )}

              {branches.map((branch, index) => {
                const branchAuth = branchTokens.find(
                  (bt) => String(bt.SuperId) === String(branch.SuperId),
                );
                const hasAccess = !!branchAuth?.Token;

                return (
                  <div
                    key={index}
                    className="col-xl-3 col-lg-4 col-md-6 col-12 d-flex"
                  >
                    <div className="card flex-fill">
                      {/* Card Header */}
                      <div className="card-header d-flex align-items-center justify-content-between py-2">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0 bg-secondary"
                            style={{ width: 38, height: 38, fontSize: 13 }}
                          >
                            {branch?.OrgName.charAt(0)}
                          </div>
                          <div>
                            <h6
                              className="mb-0 fw-semibold"
                              style={{ fontSize: 12, lineHeight: 1.3 }}
                            >
                              {branch.OrgName}
                            </h6>

                            {!hasAccess && (
                              <span
                                className="badge bg-warning text-dark"
                                style={{ fontSize: 10 }}
                              >
                                No License
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          className="btn btn-light"
                          onClick={() => handleShowPortal(branch)}
                          title={
                            hasAccess ? "Show Portal" : "No license available"
                          }
                          disabled={!hasAccess}
                          style={{ width: 32, height: 32, padding: 0 }}
                        >
                          <i
                            className={`ti ti-external-link ${hasAccess ? "text-secondary" : "text-muted"}`}
                          />
                        </button>
                      </div>

                      {/* Stats */}
                      <div className="card-body p-0">
                        <div className="d-flex justify-content-between align-items-center border-bottom px-3 py-2">
                          <span className="fw-bold fs-13">Registrations</span>
                          <span className="fw-semibold fs-13">
                            {branch.TotalRegistrations ?? 0}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center border-bottom px-3 py-2">
                          <span className="fw-bold fs-13">Present</span>
                          <span className="fw-semibold fs-13">
                            {branch.Present ?? 0}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center border-bottom px-3 py-2">
                          <span className="fw-bold fs-13">Late </span>
                          <span className="fw-semibold fs-13">
                            {branch.Late ?? 0}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center px-3 py-2">
                          <span className="fw-bold fs-13">Absent</span>
                          <span className="fw-semibold fs-13">
                            {branch.Absent ?? 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="copyright-footer d-flex align-items-center justify-content-between border-top bg-white gap-3 flex-wrap">
            <p className="fs-13 text-gray-9 mb-0">
              2014-2026 © Pereco. All Right Reserved
            </p>
            <p>
              Designed &amp; Developed By{" "}
              <a
                href="https://perennialcode.in/"
                className="link-primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Pereco
              </a>
            </p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body d-flex justify-content-center">
            <h1>You dont have access to this page</h1>
          </div>
        </div>
      )}
    </>
  );
};

export default OrgDashboard;
